import React, { useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  InputNumber,
  Select,
  Upload,
  Divider,
  Row,
  Col,
  Card,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import imageCompression from 'browser-image-compression';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../store/AuthContext';

const { TextArea } = Input;
const { Option } = Select;

interface OrderFormProps {
  editOrder?: any;
  onFinishEdit?: () => void;
  onSubmit: (order: any) => void;
}

const isEmptyObject = (obj: any): any => {
  if (!obj || typeof obj !== 'object') return true;
  return Object.values(obj).every(
    (v) =>
      v === undefined ||
      v === null ||
      (typeof v === 'string' && v.trim() === '') ||
      (typeof v === 'object' && isEmptyObject(v))
  );
};

const filterList = (arr: any[] | undefined) =>
  Array.isArray(arr) ? arr.filter((item) => !isEmptyObject(item)) : [];

const OrderForm: React.FC<OrderFormProps> = ({ editOrder, onFinishEdit, onSubmit }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const { hasPermission } = useAuth();

  useEffect(() => {
    if (editOrder) {
      form.setFieldsValue(editOrder);
      const total = Number(editOrder.totalPrice) || 0;
      const pay = Number(editOrder.pay) || 0;
      form.setFieldsValue({ remain: total - pay });
    }
  }, [editOrder, form]);

  const getBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = (error) => reject(error);
    });

  const handleValuesChange = (_: any, all: any) => {
    const total = Number(all.totalPrice) || 0;
    const pay = Number(all.pay) || 0;
    form.setFieldsValue({ remain: total - pay < 0 ? 0 : total - pay });
  };

  const onFinish = (values: any) => {
    const order = {
      ...editOrder,
      ...values,
      items: filterList(values.items),
      subOrders: filterList(values.subOrders),
      orderDetails: filterList(values.orderDetails),
    };
    Object.keys(order).forEach((key) => {
      if (
        order[key] === undefined ||
        order[key] === null ||
        (typeof order[key] === 'string' && order[key].trim() === '') ||
        (Array.isArray(order[key]) && order[key].length === 0)
      ) {
        delete order[key];
      }
    });
    onSubmit(order);
    form.resetFields();
    onFinishEdit?.();
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={onFinish}
      style={{ padding: 24 }}
      onValuesChange={handleValuesChange}
    >
      <Card title={t('order_information')} bordered={false}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="cardLink"
              label={t('card_link')}
              rules={[{ required: true }]}
            >
              <Input placeholder={t('card_link') as string} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="customerCode"
              label={t('customer_code')}
              rules={[{ required: true }]}
            >
              <Input placeholder={t('customer_code') as string} required />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('status')}
              initialValue="processing"
            >
              <Select>
                <Option value="processing">{t('processing')}</Option>
                <Option value="ordered">{t('ordered')}</Option>
                <Option value="shipped">{t('shipped')}</Option>
                <Option value="inStore">{t('in_store')}</Option>
                <Option value="delivered">{t('delivered')}</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Divider orientation="left">{t('financial_info')}</Divider>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            name="totalPrice"
            label={t('total_price')}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="pay"
            label={t('pay')}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="remain"
            label={t('remain')}
          >
            <InputNumber style={{ width: '100%' }} min={0} disabled />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="deliveryCost"
            label={t('delivery_cost')}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            name="qty"
            label={t('total_quantity')}
          >
            <InputNumber style={{ width: '100%' }} min={0} />
          </Form.Item>
        </Col>
      </Row>

      <Card title={t('order_items')} bordered={false} style={{ marginTop: 24 }}>
        <Form.List name="items">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Card
                  key={key}
                  type="inner"
                  title={`${t('item')} ${key + 1}`}
                  style={{ marginBottom: 16 }}
                  extra={<Button danger onClick={() => remove(name)}>{t('remove')}</Button>}
                >
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label={t('image')}>
                        <Form.Item noStyle>
                          <Upload
                            listType="picture-card"
                            maxCount={1}
                            accept="image/*"
                            showUploadList={{
                              showPreviewIcon: false,
                              showRemoveIcon: true,
                            }}
                            beforeUpload={async (file) => {
                              try {
                                const compressed = await imageCompression(file, {
                                  maxSizeMB: 8,
                                  maxWidthOrHeight: 1200,
                                  useWebWorker: true
                                });
                                const base64 = await getBase64(compressed);
                                const items = form.getFieldValue('items') || [];
                                items[name] = { ...items[name], image: base64 };
                                form.setFieldsValue({ items });
                              } catch (err) {
                                // Image compression failed - handled silently
                              }
                              return false;
                            }}
                            fileList={
                              form.getFieldValue('items')?.[name]?.image
                                ? [{
                                  uid: '-1',
                                  name: 'image.png',
                                  status: 'done',
                                  url: form.getFieldValue('items')[name].image
                                }]
                                : []
                            }
                            onRemove={() => {
                              const items = form.getFieldValue('items') || [];
                              items[name] = { ...items[name], image: '' };
                              form.setFieldsValue({ items });
                            }}
                          >
                            {form.getFieldValue('items')?.[name]?.image ? null : (
                              <div>
                                <UploadOutlined />
                                <div style={{ marginTop: 8 }}>{t('upload')}</div>
                              </div>
                            )}
                          </Upload>
                          {/* === Download Image Button === */}
                          {form.getFieldValue('items')?.[name]?.image && (
                            <a
                              href={form.getFieldValue('items')[name].image}
                              download={`item-image-${key + 1}.png`}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{ display: 'inline-block', marginTop: 8 }}
                            >
                              <Button type="default" block>
                                {t('download_image') || 'Download Image'}
                              </Button>
                            </a>
                          )}
                        </Form.Item>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item {...restField} name={[name, 'qty']} label={t('quantity')}>
                        <InputNumber style={{ width: '100%' }} min={1} />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item {...restField} name={[name, 'note']} label={t('note')}>
                        <TextArea rows={2} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item {...restField} name={[name, 'customization', 'note']} label={t('customization_note')}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item {...restField} name={[name, 'customization', 'detail']} label={t('customization_detail')}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block>
                  + {t('add_item')}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Card>

      <Card title={t('sub_orders')} bordered={false} style={{ marginTop: 24 }}>
        <Form.List name="subOrders">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Card
                  key={key}
                  type="inner"
                  style={{ marginBottom: 16, background: '#fafafa' }}
                  extra={<Button danger onClick={() => remove(name)}>{t('remove')}</Button>}
                >
                  <Row gutter={16}>
                    <Col span={10}>
                      <Form.Item {...restField} name={[name, 'link']} label={t('link')}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={6}>
                      <Form.Item {...restField} name={[name, 'pieces']} label={t('pieces')}>
                        <InputNumber style={{ width: '100%' }} min={1} />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item {...restField} name={[name, 'note']} label={t('note')}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block>
                  + {t('add_sub_order')}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Card>

      <Card title={t('order_details')} bordered={false} style={{ marginTop: 24 }}>
        <Form.List name="orderDetails">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <Card key={key} type="inner" style={{ marginBottom: 16, background: '#fafafa' }} extra={
                  <Button danger onClick={() => remove(name)}>{t('remove')}</Button>
                }>
                  <Row gutter={16}>
                    <Col span={8}>
                      <Form.Item {...restField} name={[name, 'orderNo']} label={t('order_no')}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item {...restField} name={[name, 'orderTrackNo']} label={t('tracking_no')}>
                        <Input />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item {...restField} name={[name, 'accountNo']} label={t('account_no')}>
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block>
                  + {t('add_order_detail')}
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Card>

      <Form.Item style={{ marginTop: 24 }}>
        <Button type="primary" htmlType="submit" block size="large">
          {editOrder ? (hasPermission("order.update") && t('update_order')) : (hasPermission("order.create") && t('create_order'))}
        </Button>
      </Form.Item>
    </Form>
  );
};

export default OrderForm;
